import type { GenerateTask } from '@studio/contract';
import { useEffect, useState } from 'react';

/**
 * Dynamic loading messages shown in the prompt panel while a generation
 * is in flight (iter 39). Ports the legacy `FooterNote` pattern:
 *
 *   - One pool of tips per `GenerateTask`. Tips alternate "Did you
 *     know?" facts with mode-specific "Tip:" pointers so the user
 *     always learns something during the wait.
 *   - Rotation cadence and fade-swap duration match the legacy
 *     architecture brief verbatim (8000 / 300 ms).
 *   - A typical 25-second generation cycles 3 tips — enough to feel
 *     alive without being chatty.
 */

/** Tip rotation interval — matches legacy `FooterNote.tsx`. */
export const TIP_ROTATION_INTERVAL_MS = 8_000;

/** Fade-out duration before the next tip becomes visible. */
export const TIP_FADE_MS = 300;

/**
 * Per-task tip pools. Each pool has at least 6 entries so users
 * generating slow batches (40 s+) don't see repeats. Mode-specific
 * facts come first; generic "Did you know?" facts close out the pool.
 * Copy length stays under ~80 chars to fit on one line of the prompt
 * panel without truncation.
 */
export const TIPS_BY_TASK: Record<GenerateTask, readonly string[]> = {
  enhance: [
    'Did you know? Empty prompts work great — the AI picks a balanced scene for you.',
    'Tip: Short, concrete prompts beat long, abstract ones every time.',
    'Tip: Use Express for catalog grids and Art Director for hero shots.',
    'Did you know? Photo Style often shapes the background more than the prompt.',
    'Tip: Raise the size tier if the result looks soft — Standard is for drafts.',
    'Did you know? Each Enhance variant uses a fresh seed so results stay diverse.',
    'Tip: Mood words ("warm", "minimalist", "editorial") translate well to lighting.',
  ],
  edit: [
    'Tip: Edit follows your prompt exactly — describe one change at a time.',
    'Did you know? Original images are never modified; edits create new drafts.',
    'Tip: "Change the background to white" works better than "make it cleaner".',
    'Did you know? Edit skips the rewrite step, so prompts hit the model verbatim.',
    'Tip: For watermark or text removal, mention the location ("top-right corner").',
    'Did you know? Multiple edits can stack — apply one change, then refine the draft.',
    'Tip: Keep prompts under 280 characters for the most consistent results.',
  ],
  'match-style': [
    'Tip: AI-generated templates unlock Match Style; uploaded photos use Match Scene.',
    'Did you know? Match Scene copies lighting and composition; Match Style copies mood.',
    'Tip: Add per-image instructions to keep one product centered or color-fixed.',
    'Did you know? You can match up to 6 product images against a single template.',
    'Tip: A clean template with no text matches more reliably than a busy one.',
    'Did you know? Match Scene uses two input images, so it costs double base credits.',
    'Tip: If colors drift, switch templates — the source carries the palette.',
  ],
  merge: [
    'Tip: Reference products by their number ("place image 1 in the center").',
    'Did you know? Merge takes 30 s or more — scaling with the number of inputs.',
    'Tip: Use Create Scene to let the AI compose; Add to Scene reuses your background.',
    'Did you know? The first 2 images are included in the base cost; extras add tier credits.',
    'Tip: Similar-sized products merge cleaner than mixed scales.',
    'Did you know? Merge supports up to 8 product images per generation.',
    'Tip: For Add to Scene, the scene image controls the background and lighting.',
  ],
};

/** Returns the tip pool for a task, falling back to enhance. */
export function getTipsForTask(task: GenerateTask): readonly string[] {
  return TIPS_BY_TASK[task] ?? TIPS_BY_TASK.enhance;
}

/**
 * Rotating-tip hook. When `isActive` is `true`, returns a `tip` string
 * that cycles through `TIPS_BY_TASK[task]` every
 * `TIP_ROTATION_INTERVAL_MS`. `visible` flips `false` for `TIP_FADE_MS`
 * around each swap so consumers can drive a CSS opacity transition.
 *
 * When `isActive` flips `false` the index resets so the next
 * generation starts fresh on tip #0.
 */
export function useRotatingTip(
  task: GenerateTask,
  isActive: boolean,
): { tip: string; visible: boolean; index: number } {
  const tips = getTipsForTask(task);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  // Reset when the task changes or the active flag flips. Without this,
  // switching modes mid-generation could leave the index pointing past
  // the end of the new pool. The body references both deps explicitly
  // so biome's exhaustive-deps rule treats them as live (without the
  // references the rule mis-flags them as unused outer-scope values).
  useEffect(() => {
    // Reference `task` + `isActive` so biome recognises both deps.
    void task;
    void isActive;
    setIndex(0);
    setVisible(true);
  }, [task, isActive]);

  // Rotation timer. Only runs while active; fade-swap is implemented
  // as a nested `setTimeout` so the index update lands in the
  // mid-fade-out window — gives the consumer a clean 300 ms blink.
  useEffect(() => {
    if (!isActive) return;
    const rotate = setInterval(() => {
      setVisible(false);
      const fade = setTimeout(() => {
        setIndex((i) => (i + 1) % tips.length);
        setVisible(true);
      }, TIP_FADE_MS);
      // Cleanup the inner timer if the interval fires again quickly
      // (only relevant under fake timers) — `clearTimeout(undefined)`
      // is a no-op, so guarding isn't required.
      return () => clearTimeout(fade);
    }, TIP_ROTATION_INTERVAL_MS);
    return () => clearInterval(rotate);
  }, [isActive, tips.length]);

  return {
    tip: tips[index] ?? tips[0] ?? '',
    visible,
    index,
  };
}
